import React, { useEffect, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Quill from 'quill';

interface RichTextEditorProps {
    value: string;
    onChange?: (value: string) => void;
    readOnly?: boolean;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({ value, onChange, readOnly = false }) => {
    const quillRef = useRef<any>(null);

    const fontSizeArray = [
        '8px',
        '9px',
        '10px',
        '11px',
        '12px',
        '14px',
        '16px',
        '18px',
        '20px',
        '22px',
        '24px',
        '26px',
        '28px',
    ];

    useEffect(() => {
        const fontSizeStyle = Quill.import('attributors/style/size');
        fontSizeStyle.whitelist = fontSizeArray;
        Quill.register(fontSizeStyle, true);

        const setTooltips = () => {
            if (readOnly) return;
            const toolbar = quillRef.current.getEditor().getModule('toolbar');

            const pickers: Record<string, string> = {
                '.ql-picker.ql-size': 'Taille de police',
                '.ql-picker.ql-header': 'Titre',
                '.ql-picker.ql-color.ql-color-picker': 'Couleur de police',
                '.ql-picker.ql-background.ql-color-picker': 'Couleur du surlignage',
                '.ql-picker.ql-font': 'Police',
                '.ql-picker.ql-align.ql-icon-picker': 'Alignement',
            };

            Object.entries(pickers).forEach(([selector, title]) => {
                toolbar.container.querySelector(selector)?.setAttribute('title', title);
            });

            const buttonTooltips: Record<string, string> = {
                'ql-bold': 'Gras (Ctrl + B)',
                'ql-italic': 'Italique (Ctrl + I)',
                'ql-underline': 'Souligné (Ctrl + U)',
                'ql-strike': 'Barré',
                'ql-link': 'Insérer un lien',
                'ql-image': 'Insérer une image',
            };

            toolbar.container.querySelectorAll('button').forEach((button) => {
                const buttonClass = button.classList[0];
                const value = button.getAttribute('value');

                if (buttonClass === 'ql-list') {
                    button.setAttribute('title', value === 'ordered' ? 'Numérotation' : 'Puces');
                } else if (buttonClass === 'ql-indent') {
                    button.setAttribute('title', value === '+1' ? 'Augmenter le retrait' : 'Diminuer le retrait');
                } else if (buttonTooltips[buttonClass]) {
                    button.setAttribute('title', buttonTooltips[buttonClass]);
                }
            });
        };

        const applyCustomStyles = () => {
            const styleSheet = document.styleSheets[0];

            fontSizeArray.forEach((size) => {
                styleSheet.insertRule(
                    `.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="${size}"]::before { content: "${size}"; }`,
                    styleSheet.cssRules.length,
                );
                styleSheet.insertRule(
                    `.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="${size}"]::before { content: "${size}"; }`,
                    styleSheet.cssRules.length,
                );
            });

            styleSheet.insertRule(
                `.ql-editor { min-height: ${readOnly ? 'auto' : '200px'}; }`,
                styleSheet.cssRules.length,
            );
        };

        setTooltips();
        applyCustomStyles();
    }, []);

    return (
        <>
            <ReactQuill
                ref={quillRef}
                theme={readOnly ? 'bubble' : 'snow'}
                value={value}
                onChange={onChange}
                readOnly={readOnly}
                modules={{
                    toolbar: readOnly
                        ? null
                        : [
                              ['bold', 'italic', 'underline', 'strike'],
                              ['link'],
                              [{ list: 'ordered' }, { list: 'bullet' }],
                              [{ indent: '-1' }, { indent: '+1' }],
                              [{ size: fontSizeArray }],
                              [{ header: [1, 2, 3, 4, 5, 6, false] }],
                              [{ color: [] }, { background: [] }],
                              [{ font: [] }],
                              [{ align: [] }],
                              ['image'],
                          ],
                }}
            />
        </>
    );
};

export default RichTextEditor;
